// Visit-report SUBMIT wrappers — the write side of the mission-visits API.
//
// Extracted from missionVisits.ts as one COHESIVE unit: the three typed
// Besuchsbericht write paths (v1.3 bespoke, v2 typed, v8 SPIN supplemental).
// They share the same envelope-unwrap idiom and the same idempotency contract
// (`source_event_id` minted once, resent verbatim on retry/replay so the SL
// collapses duplicates), so they belong together behind the missionVisits
// facade (which re-exports them — the public import path is unchanged).
//
// Each is a single call into `serviceFetch` guarded by `withWrappedError` so a
// ServiceHttpError surfaces as a friendly Error with a domain prefix.

import { serviceFetch } from '../http/serviceLayerClient'
import { ContractMismatchError } from '../http/slErrors'
import { VisitReportV8Result as VisitReportV8ResultSchema, VisitReportV8SubmitResponse as VisitReportV8SubmitResponseSchema } from '../wire/generated/schemas.generated'

// The generated response is a union (stored receipt | dead letter). The dead letter has its own reader, so what is left
// is bound to the STORED branch alone: a union reports one issue at `data`, this names the field (`data.<field>`).
const VisitReportV8StoredResponseSchema = VisitReportV8SubmitResponseSchema.extend({ data: VisitReportV8ResultSchema })
import { buildCtx, withWrappedError } from './missionVisits.helpers'
import { readDeadLetter, v8DeadLetterDrift, VisitReportDeadLetterShapeError, type DeadLetterDoor, type VisitReportDeadLetter } from './visitReportDeadLetter'
import { recordVisitReportAckMismatch, recordVisitReportDeadLetter, recordVisitReportDeadLetterDrift } from './visitReportDeadLetterTelemetry'
import { visitReportV8FinalRefusalCodeOf } from './missionVisitReports.refusals'
import type {
  VisitReportPayload,
  VisitReportSubmitResponse,
  VisitReportV2Payload,
  VisitReportV2SubmitResponse,
  VisitReportV8Payload,
  VisitReportV8Result,
} from './missionVisits.types'

export {
  VISIT_REPORT_V8_MISSION_NOT_CALLERS,
  VISIT_REPORT_V8_STOP_NOT_ON_MISSION,
  VISIT_REPORT_V8_STOP_NOT_FOUND,
  VISIT_REPORT_V8_FINAL_REFUSAL_CODES,
  visitReportV8FinalRefusalCodeOf,
  visitReportRefusalLabelOf,
  type VisitReportV8FinalRefusalCode,
} from './missionVisitReports.refusals'

export async function submitVisitReport(
  payload: VisitReportPayload,
): Promise<VisitReportSubmitResponse> {
  return withWrappedError('Failed to submit visit report', async () => {
    const ctx = await buildCtx()
    // SL response envelope is either `{ data: { id, ... } }` (current
    // shape per /v1/missions/visit-report) or `{ id, ... }` directly
    // (older / sometimes-flattened variant). Accept both. If the id is
    // missing on the wire entirely, return null — the consumer (SF-008
    // camera thread) handles the absent-FK case explicitly per D-33 R3.
    const { data } = await serviceFetch<unknown>('/v1/missions/visit-report', {
      method: 'POST',
      body: JSON.stringify(payload),
      ctx,
    })
    const envelope = (data ?? {}) as Record<string, unknown>
    const inner = ((envelope.data ?? envelope) as Record<string, unknown>) ?? {}
    const rawId = inner.visit_report_id ?? inner.id
    const visit_report_id = typeof rawId === 'string' && rawId ? rawId : null
    return { visit_report_id }
  })
}

// Typed Besuchsbericht v2 submit (POST /v1/missions/visit-report-v2). The body
// is the GENERATED VisitReportV2SubmitRequest verbatim — extra='forbid', so the
// caller must send exactly the declared keys (the typed builder guarantees it).
// `source_event_id` (in the payload) is the idempotency/dedup key: a retry or an
// offline-replay re-POSTs the SAME body verbatim, so the SL collapses duplicates.
// ⛔ THE DURABLE ID ON THIS DOOR IS `engagement_id`, NOT `visit_report_id`/`id`.
// The producer declares this door's 200 as VisitReportV2SubmitResponse =
// `{ data: { engagement_id: string, disposition?: … } }` (verified 2026-07-27
// against the SL OpenAPI spec this repo generates its wire schemas from). This
// function previously read `visit_report_id ?? id` — NEITHER key exists on that
// schema — so it returned null on every SUCCESSFUL submit, and the v2 offline
// replay handler, which refuses to prune an outbox entry without a durable id,
// threw on every replay of a write that had in fact landed. The v8 twin already
// did this correctly with `engagement_id`; the cure shipped to one door and not
// the other. Those phantom keys are deliberately NOT retained as a fallback:
// they are not in the producer contract, and keeping them would re-hide which
// field is load-bearing (CODING-STANDARDS §2).
//
// Absent engagement_id (no dead letter) → VisitReportResponseShapeError, never null: a null id
// with no queued reason read as v2 CONFIRMED and v8 was POSTed (Codex #2209 P1). FINAL, not
// retried — the receipt contract is broken, as on the v8 door (D-33 R3: no manufactured proof).
// The returned field keeps its `visit_report_id` name: it is this door's durable
// server-assigned row identity, which is what every consumer uses it as.
export async function submitVisitReportV2(
  payload: VisitReportV2Payload,
): Promise<VisitReportV2SubmitResponse> {
  return withWrappedError('Failed to submit visit report', async () => {
    const ctx = await buildCtx()
    const { data } = await serviceFetch<unknown>('/v1/missions/visit-report-v2', {
      method: 'POST',
      body: JSON.stringify(payload),
      ctx,
    })
    const inner = envelopeData(data)
    // RUNG 4 of the resolver: success, NO engagement, the report held as a dead letter. On THIS door the
    // dead letter sits BESIDE `data` on the response body, never inside it (visitReportDeadLetter).
    const dead_letter = deadLetterOfThisWrite('v2', data, payload.source_event_id)
    if (dead_letter !== null) return { visit_report_id: null, dead_letter }
    const rawId = inner === null ? undefined : inner.engagement_id
    if (typeof rawId !== 'string' || rawId === '') {
      const missed = inner === null ? 'data' : 'engagement_id'
      throw new VisitReportResponseShapeError('v2', fieldsOf(inner), [missed], `v2 submit response missing the required \`${missed}\``)
    }
    return { visit_report_id: rawId, dead_letter: null }
  })
}

/** The `data` object of the SL success envelope `{ success, data }`; null when absent. */
function envelopeData(body: unknown): Record<string, unknown> | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null
  const data = (body as { data?: unknown }).data
  return data !== null && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null
}

// A dead letter names the write the server holds by its `source_event_id`. It acknowledges THIS
// request only when that key is the request's own (Codex #2209 P2): one naming another key says
// nothing about this write, and accepting it would prune the outbox entry — and the replay copy —
// of a write the server never confirmed. Not in the generated wire types; defined here once, next
// to the door clients, like the final-refusal codes below.
export const VISIT_REPORT_ACK_SOURCE_EVENT_MISMATCH_CODES = {
  v2: 'VISIT_REPORT_V2_ACK_SOURCE_EVENT_MISMATCH',
  v8: 'VISIT_REPORT_V8_ACK_SOURCE_EVENT_MISMATCH',
} as const satisfies Record<DeadLetterDoor, string>

/** A door's dead letter named ANOTHER write's key: the answer breaks the door's response contract
 *  (an acknowledgement names the request's own key), and the server dedups on source_event_id, so a
 *  re-send of the same body gets the same answer (review R2). A ContractMismatchError for that reason:
 *  the generic classifier (retryClassification.isRetryable) never queues or retries it — online it is
 *  a hard failure through the door's hard-error sink, drained it is pinned on its first replay (the
 *  replay handlers re-throw it as a FinalRefusalError by its code). The write stays UNCONFIRMED. */
export class VisitReportAckSourceEventMismatchError extends ContractMismatchError {
  readonly code: (typeof VISIT_REPORT_ACK_SOURCE_EVENT_MISMATCH_CODES)[DeadLetterDoor]
  constructor(
    readonly door: DeadLetterDoor,
    readonly requestSourceEventId: string,
    readonly answeredSourceEventId: string,
    readonly deadLetterReasonCode: string,
  ) {
    super({
      slug: `visit-report-${door}`,
      expectedShape: `a dead letter for source_event_id ${requestSourceEventId}`,
      actualShape: `a dead letter for source_event_id ${answeredSourceEventId}`,
      rawData: { source_event_id: answeredSourceEventId, reason_code: deadLetterReasonCode },
      message: `visit-report ${door} door answered a dead letter for source_event_id ${answeredSourceEventId}, `
        + `not for this write's ${requestSourceEventId} — not an acknowledgement; the write stays unconfirmed`,
    })
    this.name = 'VisitReportAckSourceEventMismatchError'
    this.code = VISIT_REPORT_ACK_SOURCE_EVENT_MISMATCH_CODES[door]
  }
}

// Review F1 — a door's 200 that matches NEITHER branch of its contract: not the generated receipt, not the declared
// dead letter (answers_dead_lettered stays the v8 flag; no other key is read as one). The deployed v8 door answered a
// dead letter without dead_lettered / engagement_id / reason_code / source_event_id: a plain Error with no status, so it
// was queued, retried on every flush, re-pended on every app load, and the rep told "wird erneut gesendet".
export const VISIT_REPORT_RESPONSE_SHAPE_INVALID_CODES = {
  v2: 'VISIT_REPORT_V2_RESPONSE_SHAPE_INVALID',
  v8: 'VISIT_REPORT_V8_RESPONSE_SHAPE_INVALID',
} as const satisfies Record<DeadLetterDoor, string>

/** That answer as a ContractMismatchError: never queued or retried (retryClassification.isRetryable), FINAL by its code
 *  on its own door (visitReportFinalCodeOf) — the server dedups on the key, a re-send meets the same door. The write
 *  stays UNCONFIRMED. Carries field NAMES only, never a value (no case data): the transport captures nothing itself —
 *  the sink that meets it does, once (online the hard-error sink, drained the retained copy of the pinned entry). */
export class VisitReportResponseShapeError extends ContractMismatchError {
  readonly code: (typeof VISIT_REPORT_RESPONSE_SHAPE_INVALID_CODES)[DeadLetterDoor]
  constructor(
    readonly door: DeadLetterDoor,
    readonly responseFields: readonly string[],
    readonly invalidFields: readonly string[],
    problem: string,
  ) {
    const code = VISIT_REPORT_RESPONSE_SHAPE_INVALID_CODES[door]
    super({
      slug: `visit-report-${door}`,
      expectedShape: `VisitReport${door.toUpperCase()}SubmitResponse or the declared dead letter`,
      actualShape: `data fields [${responseFields.join(', ')}]`,
      rawData: { response_fields: responseFields, invalid_fields: invalidFields },
      message: `${problem} — answered data fields [${responseFields.join(', ')}]: neither the receipt nor the declared `
        + `dead letter, the write stays unconfirmed (${code})`,
    })
    this.name = 'VisitReportResponseShapeError'
    this.code = code
  }
}

const fieldsOf = (data: unknown): string[] => (data !== null && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : [])

/**
 * The dead letter in a door's answer when it acknowledges THIS request (recorded once), null when the
 * answer is no dead letter. `answer` is what readDeadLetter reads on that door: the `data` object on v8,
 * the whole response body on v2. THROWS — captured once, nothing recorded as a dead letter — when it
 * names another key; a half-shaped dead letter is a VisitReportResponseShapeError (review F1).
 */
function deadLetterOfThisWrite(
  door: DeadLetterDoor, answer: unknown, requestSourceEventId: string,
): VisitReportDeadLetter | null {
  let deadLetter: VisitReportDeadLetter | null
  try {
    deadLetter = readDeadLetter(door, answer)
  } catch (err) {
    if (err instanceof VisitReportDeadLetterShapeError) throw new VisitReportResponseShapeError(door, fieldsOf(answer), err.invalidFields, err.message)
    throw err
  }
  if (deadLetter === null) return null
  if (deadLetter.sourceEventId !== requestSourceEventId) {
    const mismatch = new VisitReportAckSourceEventMismatchError(
      door, requestSourceEventId, deadLetter.sourceEventId, deadLetter.reasonCode,
    )
    recordVisitReportAckMismatch(mismatch)
    throw mismatch
  }
  recordVisitReportDeadLetter(deadLetter)
  if (door === 'v8') { // held is held: a body short of the generated model is reported, never refused (visitReportDeadLetter)
    const drift = v8DeadLetterDrift(answer)
    if (drift.invalid.length > 0 || drift.undeclared.length > 0) recordVisitReportDeadLetterDrift(deadLetter, drift)
  }
  return deadLetter
}

/** The code a failed call of `door` is FINAL under (re-sending the same body cannot change the answer),
 *  or null. On EITHER door: its acknowledgement-mismatch and response-shape codes. On the v8 door only: the three
 *  SL #2682 refusals. Every other failure keeps the generic status rule. Read by the outbox replay handlers. */
export function visitReportFinalCodeOf(door: DeadLetterDoor, err: unknown): string | null {
  if (err instanceof VisitReportAckSourceEventMismatchError && err.door === door) return err.code
  if (err instanceof VisitReportResponseShapeError && err.door === door) return err.code
  return door === 'v8' ? visitReportV8FinalRefusalCodeOf(err) : null
}

/** The v8 door's answer: the stored receipt, or the report held as a dead letter. */
export type VisitReportV8Outcome =
  | { kind: 'stored'; result: VisitReportV8Result }
  | VisitReportDeadLetter

// Besuchsbericht v8 ANSWERS submit (POST /v1/missions/visit-report-v8 — the provider
// routes it to public.rpc_submit_visit_v8_answers). `source_event_id` resolves the
// visit engagement minted by the v2 report; a retry re-POSTs the SAME body and the
// door collapses duplicates.
//
// TWO ACCEPTED ANSWERS, NOTHING ELSE: (a) the stored receipt — a REQUIRED row-tally
// parsed through the GENERATED VisitReportV8SubmitResponse; (b) a DEAD LETTER (the lead
// resolves to no account — RUNG 4): success with no engagement, the report held
// server-side (visitReportDeadLetter, a declared consumer expectation). Anything else is
// a LOUD failure (D-33 R1): we never fabricate a tally.
export async function submitVisitReportV8(
  payload: VisitReportV8Payload,
): Promise<VisitReportV8Outcome> {
  return withWrappedError('Failed to submit visit report (v8)', async () => {
    const ctx = await buildCtx()
    // The SL envelope is `{ success: true, data: VisitReportV8Result }`.
    const { data } = await serviceFetch<unknown>(
      '/v1/missions/visit-report-v8',
      {
        method: 'POST',
        body: JSON.stringify(payload),
        ctx,
      },
    )
    const result = envelopeData(data)
    const deadLetter = deadLetterOfThisWrite('v8', result, payload.source_event_id)
    if (deadLetter !== null) return deadLetter
    if (result === null) {
      // No row-tally in the response ⇒ the write did NOT confirm. Fail loud
      // rather than return a manufactured empty tally (D-33 R1/R3) — final, as every contract breach (F1).
      throw new VisitReportResponseShapeError('v8', [], ['data'], 'v8 submit response missing the required `data` row-tally')
    }
    // PARSE, never cast: bind the envelope to the GENERATED producer response
    // schema (SL OpenAPI SSoT). A cast let a tally with a missing engagement_id,
    // a string count or a negative count through as a "confirmed" write; the
    // offline handler prunes its outbox entry on that id, so a malformed 200
    // must fail loud instead (D-33 R1). The generated object is non-strict, so
    // producer keys the vendored contract does not yet declare are stripped,
    // not refused — adding a receipt field SL-side never breaks this client.
    // A miss is a contract breach (F1): FINAL, and named by PATH only — a zod message can quote a value.
    const parsed = VisitReportV8StoredResponseSchema.safeParse(data)
    if (!parsed.success) {
      const invalid = [...new Set(parsed.error.issues.map((i) => i.path.join('.') || '(root)'))]
      throw new VisitReportResponseShapeError(
        'v8', fieldsOf(result), invalid, `v8 submit response does not match VisitReportV8SubmitResponse — ${invalid.join(', ')}`,
      )
    }
    return { kind: 'stored', result: parsed.data.data }
  })
}
